/**
 * When applying a library patient template, swap that patient's name / id placeholders
 * for pronouns based on the custom patient's sex (Personal tab).
 */

function escapeRegex(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Longest-first name phrases (full name, first, last). Mirrors intent name phrase helper. */
export function buildTemplateSourceNamePhrases(rawName) {
  const name = String(rawName || '').trim();
  if (!name) return [];
  const lower = name
    .toLowerCase()
    .replace(/\./g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  let parts = lower.split(/\s+/).filter(Boolean);
  const titles = new Set(['dr', 'doc', 'mr', 'mrs', 'ms', 'miss']);
  while (parts.length && titles.has(parts[0])) parts = parts.slice(1);
  while (parts.length && titles.has(parts[parts.length - 1])) parts = parts.slice(0, -1);
  parts = parts.filter((t) => t.length >= 2);
  if (parts.length === 0) return [];
  const phrases = new Set();
  if (parts.length >= 2) {
    phrases.add(parts.join(' '));
    phrases.add(parts[0]);
    phrases.add(parts[parts.length - 1]);
  } else {
    phrases.add(parts[0]);
  }
  return [...phrases].sort((a, b) => b.length - a.length);
}

export function pronounsForSex(sex) {
  const value = String(sex || '').trim().toLowerCase();
  if (value === 'female') {
    return {
      subject: 'she',
      object: 'her',
      possessive: 'her',
      reflexive: 'herself',
      Subject: 'She',
      Object: 'Her',
      Possessive: 'Her',
    };
  }
  if (value === 'male') {
    return {
      subject: 'he',
      object: 'him',
      possessive: 'his',
      reflexive: 'himself',
      Subject: 'He',
      Object: 'Him',
      Possessive: 'His',
    };
  }
  return null;
}

function capitalizeAt(text, index, pronounPair) {
  if (index <= 0) return pronounPair.Subject;
  const before = text.slice(0, index);
  if (/[.!?]\s*$/.test(before) || /[\n\r]\s*$/.test(before)) return pronounPair.Subject;
  return pronounPair.subject;
}

function capitalizePossessiveAt(text, index, pronounPair) {
  if (index <= 0) return pronounPair.Possessive;
  const before = text.slice(0, index);
  if (/[.!?]\s*$/.test(before) || /[\n\r]\s*$/.test(before)) return pronounPair.Possessive;
  return pronounPair.possessive;
}

function phraseBodyRegex(phrase) {
  const parts = String(phrase || '').split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  return parts.map((part) => `\\b${escapeRegex(part)}\\b`).join('\\s+');
}

function buildReplacementRules(sourceName, sourceCharacterId) {
  /** @type {Array<{ pattern: RegExp, form: 'subject'|'possessive' }>} */
  const rules = [];

  const id = sourceCharacterId != null ? String(sourceCharacterId).trim() : '';
  if (/^\d+$/.test(id)) {
    const idEsc = escapeRegex(id);
    rules.push({
      pattern: new RegExp(`\\bpatient\\s*#?\\s*${idEsc}['\u2019]s\\b`, 'gi'),
      form: 'possessive',
    });
    rules.push({
      pattern: new RegExp(`\\bpatient\\s*#?\\s*${idEsc}\\b`, 'gi'),
      form: 'subject',
    });
    rules.push({
      pattern: new RegExp(`\\bpatient${idEsc}['\u2019]s\\b`, 'gi'),
      form: 'possessive',
    });
    rules.push({
      pattern: new RegExp(`\\bpatient${idEsc}\\b`, 'gi'),
      form: 'subject',
    });
  }

  buildTemplateSourceNamePhrases(sourceName).forEach((phrase) => {
    const body = phraseBodyRegex(phrase);
    if (!body) return;
    rules.push({
      pattern: new RegExp(`${body}['\u2019]s\\b`, 'gi'),
      form: 'possessive',
    });
    rules.push({
      pattern: new RegExp(body, 'gi'),
      form: 'subject',
    });
  });

  rules.push({ pattern: /\bunfixed patient['\u2019]s\b/gi, form: 'possessive' });
  rules.push({ pattern: /\bunfixed patient\b/gi, form: 'subject' });

  return rules;
}

export function replaceTemplateTextWithPronouns(text, { sourceName, sourceCharacterId, sex } = {}) {
  const pronouns = pronounsForSex(sex);
  const source = text == null ? '' : String(text);
  if (!source || !pronouns) return source;

  const rules = buildReplacementRules(sourceName, sourceCharacterId);
  let out = source;
  rules.forEach((rule) => {
    out = out.replace(rule.pattern, (match, offset) => (
      rule.form === 'possessive'
        ? capitalizePossessiveAt(out, offset, pronouns)
        : capitalizeAt(out, offset, pronouns)
    ));
  });
  return out;
}

function personalizeString(value, context) {
  if (typeof value !== 'string' || !value) return value;
  return replaceTemplateTextWithPronouns(value, context);
}

function personalizeRow(row, context, resultKey = 'result') {
  if (!row || typeof row !== 'object') return row;
  const result = row[resultKey];
  if (typeof result !== 'string' || !result) return row;
  return { ...row, [resultKey]: replaceTemplateTextWithPronouns(result, context) };
}

/** Adjust integrated template copy for the custom patient's sex on the Personal tab. */
export function personalizeIntegratedTemplatePayload(payload, sex) {
  if (!payload || typeof payload !== 'object') return payload;
  const context = {
    sourceName: payload.name,
    sourceCharacterId: payload.characterId,
    sex,
  };
  const pronouns = pronounsForSex(sex);
  if (!pronouns) return payload;

  const health = payload.health && typeof payload.health === 'object'
    ? Object.fromEntries(
      Object.entries(payload.health).map(([key, value]) => [
        key,
        typeof value === 'string' ? personalizeString(value, context) : value,
      ])
    )
    : payload.health;

  const pathologySource = payload.pathology && typeof payload.pathology === 'object'
    ? payload.pathology
    : null;
  let pathology = pathologySource;
  if (pathologySource) {
    pathology = {
      ...pathologySource,
      chiefComplaint: personalizeString(pathologySource.chiefComplaint, context),
      historyOfCurrentIllness: personalizeString(pathologySource.historyOfCurrentIllness, context),
      symptom: personalizeString(pathologySource.symptom, context),
      vitals: pathologySource.vitals,
      imaging: Array.isArray(pathologySource.imaging)
        ? pathologySource.imaging.map((row) => personalizeRow(row, context))
        : pathologySource.imaging,
      labs: Array.isArray(pathologySource.labs)
        ? pathologySource.labs.map((row) => personalizeRow(row, context))
        : pathologySource.labs,
      exams: Array.isArray(pathologySource.exams)
        ? pathologySource.exams.map((row) => personalizeRow(row, context))
        : pathologySource.exams,
    };
  }

  return { ...payload, health, pathology };
}
